import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { COA_TYPE_ORDER, COA_TYPE_LABELS, COA_TYPE_BADGE_CLASS } from './accountingUiConstants';

const TYPE_OPTIONS = [
  { value: 'asset', label: 'Aset' },
  { value: 'liability', label: 'Kewajiban' },
  { value: 'equity', label: 'Ekuitas' },
  { value: 'revenue', label: 'Pendapatan' },
  { value: 'expense', label: 'Beban' },
];

export default function AccountingCoa() {
  const [coa, setCoa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [newAcc, setNewAcc] = useState({ code: '', name: '', type: 'expense' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/accounting/coa');
      setCoa(data.accounts || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Gagal memuat COA'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredGrouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (a) =>
      !q ||
      String(a.code).toLowerCase().includes(q) ||
      String(a.name).toLowerCase().includes(q) ||
      String(COA_TYPE_LABELS[a.type] || a.type || '').toLowerCase().includes(q);

    const sortCode = (a, b) => String(a.code).localeCompare(String(b.code), 'id');
    const main = COA_TYPE_ORDER.map((type) => ({
      type,
      label: COA_TYPE_LABELS[type],
      accounts: coa.filter((a) => a.type === type && match(a)).sort(sortCode),
    })).filter((g) => g.accounts.length > 0);

    const other = coa
      .filter((a) => !COA_TYPE_ORDER.includes(a.type) && match(a))
      .sort(sortCode);
    if (other.length) {
      main.push({ type: '_other', label: 'Lainnya', accounts: other });
    }
    return main;
  }, [coa, filter]);

  const totalShown = useMemo(() => filteredGrouped.reduce((s, g) => s + g.accounts.length, 0), [filteredGrouped]);

  const seed = async () => {
    try {
      await api.post('/tenant/accounting/coa/seed');
      toast.success('COA default dibuat');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed gagal');
    }
  };

  const addCoa = async (e) => {
    e.preventDefault();
    const code = newAcc.code.trim();
    const name = newAcc.name.trim();
    if (!code || !name) {
      toast.error('Kode dan nama akun wajib diisi.');
      return;
    }
    try {
      await api.post('/tenant/accounting/coa', { ...newAcc, code, name });
      toast.success('Akun ditambah');
      setNewAcc({ code: '', name: '', type: 'expense' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menambah akun');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2D6CDF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-coa-page">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={load}>
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Muat ulang
        </Button>
        <Button size="sm" variant="secondary" type="button" onClick={seed}>
          Isi COA default
        </Button>
      </div>

      <Card className="overflow-hidden border-slate-200/90 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="text-lg text-slate-900">Bagan akun (chart of accounts)</CardTitle>
              <CardDescription className="mt-1.5 max-w-2xl leading-relaxed">
                Akun dikelompokkan menurut tipe laporan keuangan. Gunakan kode unik (mis. 1100) dan nama yang konsisten dengan buku Anda.
              </CardDescription>
            </div>
            <div className="relative w-full max-w-sm lg:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="h-10 border-slate-200 bg-white pl-9"
                placeholder="Cari kode atau nama…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                aria-label="Filter bagan akun"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!coa.length && (
            <p className="px-6 py-12 text-center text-sm text-slate-500">
              Belum ada akun. Klik <strong>Isi COA default</strong> untuk membuat kerangka standar, lalu sesuaikan di bawah.
            </p>
          )}

          {!!coa.length && !totalShown && (
            <p className="px-6 py-10 text-center text-sm text-slate-400">Tidak ada akun yang cocok dengan pencarian.</p>
          )}

          {!!coa.length && totalShown > 0 && (
            <div className="rounded-b-xl">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 bg-slate-50/90 hover:bg-slate-50/90">
                    <TableHead className="w-[110px] pl-4 text-slate-700">Kode</TableHead>
                    <TableHead className="text-slate-700">Nama akun</TableHead>
                    <TableHead className="w-[140px] text-slate-700">Tipe</TableHead>
                    <TableHead className="w-[100px] pr-4 text-slate-700">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGrouped.map((group) => (
                    <Fragment key={group.type}>
                      <TableRow className="border-slate-200 bg-slate-100/80 hover:bg-slate-100/80">
                        <TableCell colSpan={4} className="py-2 pl-4 text-xs font-bold uppercase tracking-wider text-slate-600">
                          {group.label}
                        </TableCell>
                      </TableRow>
                      {group.accounts.map((a) => (
                        <TableRow key={a.id} className="border-slate-100">
                          <TableCell className="pl-4 font-mono text-sm font-semibold text-slate-800">{a.code}</TableCell>
                          <TableCell className="text-slate-800">{a.name}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={cn(
                                'border font-medium capitalize',
                                COA_TYPE_BADGE_CLASS[a.type] || 'border-slate-200 bg-slate-50 text-slate-700',
                              )}
                            >
                              {COA_TYPE_LABELS[a.type] || a.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="pr-4">
                            {a.is_active === false ? (
                              <span className="text-xs font-medium text-amber-700">Nonaktif</span>
                            ) : (
                              <span className="text-xs font-medium text-emerald-700">Aktif</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
              <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
                Menampilkan <strong className="text-slate-700">{totalShown}</strong> akun
                {filter.trim() ? ' (sesuai filter)' : ''}.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-slate-200/90 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-4">
          <CardTitle className="text-lg text-slate-900">Tambah akun baru</CardTitle>
          <CardDescription>Lengkapi field berikut. Kode harus unik dalam tenant.</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={addCoa} className="space-y-6">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-12">
              <div className="space-y-2 sm:col-span-1 lg:col-span-2">
                <Label htmlFor="coa-code" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Kode akun
                </Label>
                <Input
                  id="coa-code"
                  className="h-10 font-mono"
                  placeholder="6100"
                  value={newAcc.code}
                  onChange={(e) => setNewAcc({ ...newAcc, code: e.target.value })}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2 sm:col-span-1 lg:col-span-6">
                <Label htmlFor="coa-name" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nama akun
                </Label>
                <Input
                  id="coa-name"
                  className="h-10"
                  placeholder="Contoh: Beban listrik & air"
                  value={newAcc.name}
                  onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 lg:col-span-4">
                <Label htmlFor="coa-type" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tipe / klasifikasi
                </Label>
                <Select value={newAcc.type} onValueChange={(v) => setNewAcc({ ...newAcc, type: v })}>
                  <SelectTrigger id="coa-type" className="h-10 border-slate-200 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                <Plus className="mr-1.5 h-4 w-4" />
                Simpan akun
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
